import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { MaterialSymbol } from '../icons/MaterialSymbol';

type Placement = 'bottom-start' | 'top-start' | 'right-start';

type AnchorRect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

type MenuContextValue = {
  close: () => void;
};

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('WebMenu item used outside <WebMenu>');
  return ctx;
}

function getAnchorRect(anchor: HTMLElement | DOMRect | null): AnchorRect | null {
  if (!anchor) return null;
  if (anchor instanceof DOMRect || (typeof DOMRect !== 'undefined' && anchor instanceof DOMRect)) {
    const r = anchor as DOMRect;
    return {
      top: r.top,
      left: r.left,
      right: r.right,
      bottom: r.bottom,
      width: r.width,
      height: r.height,
    };
  }
  const r = (anchor as HTMLElement).getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  };
}

type WebMenuProps = {
  open: boolean;
  onClose: () => void;
  anchor: HTMLElement | null;
  placement?: Placement;
  minWidth?: number;
  children: ReactNode;
  style?: CSSProperties;
  /**
   * At phone widths, present as a bottom sheet instead of an anchored popover.
   * A menu with several tall rows anchored to a control near the bottom of a
   * phone screen ends up covering the whole viewport at an arbitrary offset;
   * a sheet is the shape the rest of the app already uses there.
   */
  mobileSheet?: boolean;
  /**
   * One gliding highlight instead of a background per row: a single span
   * follows the hovered item (220ms on `top` and `height`) and rests on the
   * checked item, visible only while the pointer is over the list.
   */
  glide?: boolean;
};

const PHONE_QUERY = '(max-width: 767px)';

function usePhoneViewport(enabled: boolean) {
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const query = window.matchMedia(PHONE_QUERY);
    const sync = () => setIsPhone(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, [enabled]);

  return isPhone;
}

/**
 * Gemini-style dropdown menu.
 * - Fixed position, 24px radius, --menu-bg.
 * - Show via `display`/none: no transition, no fade, no transform.
 * - 8px from anchor; auto-flips if it would overflow the viewport.
 * - Closes on outside click or Escape.
 */
export function WebMenu({
  open,
  onClose,
  anchor,
  placement = 'bottom-start',
  minWidth = 280,
  children,
  style,
  mobileSheet = false,
  glide = false,
}: WebMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const asSheet = usePhoneViewport(mobileSheet);
  const [hovered, setHovered] = useState<HTMLElement | null>(null);
  const [glideBox, setGlideBox] = useState<{ top: number; height: number } | null>(
    null,
  );

  // The highlight rests on the checked row and follows the hovered one.
  useLayoutEffect(() => {
    if (!glide || !open) {
      setGlideBox(null);
      return;
    }
    const target =
      hovered ??
      menuRef.current?.querySelector<HTMLElement>(
        '[role^="menuitem"][aria-checked="true"], [role^="menuitem"].checked',
      ) ??
      null;
    const next = target
      ? { top: target.offsetTop, height: target.offsetHeight }
      : null;
    setGlideBox((current) =>
      current?.top === next?.top && current?.height === next?.height
        ? current
        : next,
    );
  }, [glide, open, hovered, coords, children]);

  useEffect(() => {
    if (!open) setHovered(null);
  }, [open]);

  // Position the menu relative to the anchor whenever it opens or anchor moves.
  // Skipped in sheet mode, where CSS pins the panel to the bottom edge.
  useLayoutEffect(() => {
    if (!open || asSheet) {
      setCoords(null);
      return;
    }
    const anchorRect = getAnchorRect(anchor);
    if (!anchorRect) return;

    const place = () => {
      const menu = menuRef.current;
      const menuRect = menu?.getBoundingClientRect();
      const menuW = menuRect?.width ?? minWidth;
      const menuH = menuRect?.height ?? 0;
      const margin = 8;

      let top = 0;
      let left = anchorRect.left;

      if (placement === 'bottom-start') {
        top = anchorRect.bottom + 4;
      } else if (placement === 'top-start') {
        top = anchorRect.top - 8 - menuH;
      } else if (placement === 'right-start') {
        top = anchorRect.top;
        left = anchorRect.right + 4;
      }

      // Clamp into viewport.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (left + menuW > vw - margin) left = vw - menuW - margin;
      if (left < margin) left = margin;
      if (top + menuH > vh - margin) top = Math.max(margin, vh - menuH - margin);
      if (top < margin) top = margin;

      setCoords({ top, left });
    };

    // First pass with no menu dimensions, then re-measure once the menu is in
    // the DOM so we can clamp accurately.
    place();
    const id = window.requestAnimationFrame(place);
    return () => window.cancelAnimationFrame(id);
  }, [open, anchor, placement, minWidth, asSheet]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const menu = menuRef.current;
      if (menu && menu.contains(e.target as Node)) return;
      if (anchor && anchor.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open, onClose, anchor]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const ctx = useMemo<MenuContextValue>(
    () => ({ close: onClose }),
    [onClose],
  );

  if (!open) return null;

  const content = (
    <MenuContext.Provider value={ctx}>
      {asSheet ? (
        <div className="tabbio-menu-sheet-scrim" onPointerDown={onClose} />
      ) : null}
      <div
        ref={menuRef}
        role="menu"
        className={[
          'tabbio-menu',
          asSheet ? 'tabbio-menu-sheet' : null,
          glide ? 'is-glide' : null,
          'show',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseOver={
          glide
            ? (event) => {
                const item = (event.target as Element).closest<HTMLElement>(
                  '[role^="menuitem"]',
                );
                if (item && menuRef.current?.contains(item)) setHovered(item);
              }
            : undefined
        }
        onMouseLeave={glide ? () => setHovered(null) : undefined}
        style={
          asSheet
            ? style
            : {
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                minWidth,
                // Position is clamped to the viewport above, but width was
                // not — a wide menu still ran off the edge on small screens.
                maxWidth: 'calc(100vw - 16px)',
                ...style,
              }
        }
      >
        {glide ? (
          <span
            aria-hidden
            className="tabbio-menu-glide"
            style={{
              top: glideBox?.top ?? 0,
              height: glideBox?.height ?? 0,
              opacity: glideBox && hovered ? 1 : 0,
            }}
          />
        ) : null}
        {children}
      </div>
    </MenuContext.Provider>
  );
  // Phone shells can establish a containing block for fixed children. Put
  // sheets at document level so bottom and width follow the viewport.
  return asSheet ? createPortal(content, document.body) : content;
}

// ─── Items ───────────────────────────────────────────────────────────

type MenuItemProps = {
  icon?: string;
  label: string;
  onSelect?: () => void;
  checked?: boolean;
  trailing?: ReactNode;
  badge?: string;
  hasSubmenu?: boolean;
  closeOnSelect?: boolean;
  disabled?: boolean;
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
};

export function WebMenuItem({
  icon,
  label,
  onSelect,
  checked,
  trailing,
  badge,
  hasSubmenu,
  closeOnSelect = true,
  disabled,
  onMouseEnter,
  onMouseLeave,
}: MenuItemProps) {
  const { close } = useMenuContext();

  const handleClick = useCallback(() => {
    if (disabled) return;
    onSelect?.();
    if (closeOnSelect) close();
  }, [onSelect, closeOnSelect, close, disabled]);

  return (
    <button
      type="button"
      role="menuitem"
      className={checked ? 'tabbio-menu-item checked' : 'tabbio-menu-item'}
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={disabled ? { opacity: 0.5, cursor: 'default' } : undefined}
    >
      {icon ? <MaterialSymbol name={icon} /> : null}
      <span className="tabbio-menu-label">{label}</span>
      {badge ? <span className="tabbio-menu-badge">{badge}</span> : null}
      {trailing ? (
        trailing
      ) : checked ? (
        <MaterialSymbol name="check" className="tabbio-menu-check" />
      ) : hasSubmenu ? (
        <MaterialSymbol name="chevron_right" className="tabbio-menu-submenu-arrow" />
      ) : null}
    </button>
  );
}

type ModelOptionProps = {
  title: string;
  description?: string;
  /** Visual first line; `title` remains the accessible text fallback. */
  primary?: ReactNode;
  /** Preferred leading slot for the current shared model-brand mark. */
  leading?: ReactNode;
  checked?: boolean;
  badge?: string;
  /** Accent "New" mark, rendered before `badge` so a row can wear both. */
  newBadge?: string;
  /** Backward-compatible alias used by older picker callers. */
  mark?: ReactNode;
  onSelect?: () => void;
  closeOnSelect?: boolean;
};

export function WebMenuModelOption({
  title,
  description,
  primary,
  leading,
  checked,
  badge,
  newBadge,
  mark,
  onSelect,
  closeOnSelect = true,
}: ModelOptionProps) {
  const { close } = useMenuContext();
  const leadingContent = leading ?? mark;
  const handleClick = () => {
    onSelect?.();
    if (closeOnSelect) close();
  };
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      className={checked ? 'tabbio-model-option checked' : 'tabbio-model-option'}
      onClick={handleClick}
    >
      {/* The brand leads and the selection check trails. Keeping a single
          leading column avoids the large empty inset created by two columns. */}
      {leadingContent ? (
        <span className="tabbio-model-option-mark">{leadingContent}</span>
      ) : null}
      <div className="tabbio-model-option-body">
        <div className="tabbio-model-option-title">{primary ?? title}</div>
        {description ? (
          <div
            className={
              primary
                ? 'tabbio-model-option-desc wordmark'
                : 'tabbio-model-option-desc'
            }
          >
            {description}
          </div>
        ) : null}
      </div>
      {newBadge ? (
        <span className="tabbio-menu-badge new">{newBadge}</span>
      ) : null}
      {badge ? <span className="tabbio-menu-badge">{badge}</span> : null}
      <span className="tabbio-model-check-col">
        <MaterialSymbol name="check" />
      </span>
    </button>
  );
}

type TagOptionProps = {
  title: string;
  description?: string;
  /** Plain text on the right, such as the plan a model belongs to. */
  tag?: string;
  /** A plan badge: the row is locked behind that plan. */
  badge?: string;
  newBadge?: string;
  checked?: boolean;
  tooltip?: string;
  onSelect?: () => void;
};

/**
 * A compact single-line choice: the name, a tag on the right and a check on
 * the selected row. The composer's model menu is built from these.
 */
export function WebMenuTagOption({
  title,
  description,
  tag,
  badge,
  newBadge,
  checked,
  tooltip,
  onSelect,
}: TagOptionProps) {
  const { close } = useMenuContext();
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      title={tooltip}
      className={checked ? 'tabbio-menu-tag-option checked' : 'tabbio-menu-tag-option'}
      onClick={() => {
        onSelect?.();
        close();
      }}
    >
      <span className="tabbio-menu-tag-main">
        <span className="tabbio-menu-tag-title">{title}</span>
        {description ? <span className="tabbio-menu-tag-description">{description}</span> : null}
      </span>
      {newBadge ? <span className="tabbio-menu-badge new">{newBadge}</span> : null}
      {badge ? <span className="tabbio-menu-badge">{badge}</span> : null}
      {tag ? <span className="tabbio-menu-tag">{tag}</span> : null}
      <MaterialSymbol name="check" className="tabbio-menu-tag-check" />
    </button>
  );
}

export function WebMenuDivider() {
  return <div className="tabbio-menu-divider" />;
}

export function WebMenuFooter({ children }: { children: ReactNode }) {
  return <div className="tabbio-menu-footer">{children}</div>;
}
